import { chunkPairs } from "@memorang/helpers/array";
import { formatSecondsToUTCtime } from "@memorang/helpers/common";
import type { BaseItemSchema, MixedItemTag } from "@memorang/types";
import { useTranslation } from "react-i18next";
import { useProficiencyIconMap } from "../../../hooks/useProficiencyIconMap";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import { StatTile } from "../../StatTile";
import TooltipWrapper from "../../TooltipWrapper";
import {
	type OverallPoints,
	type StatsRowTile,
	buildStatsRowTiles,
} from "./stats-row";

type Props = {
	isSummativeTest: boolean;
	tags: MixedItemTag[];
	topics: string[];
	sessionItems: BaseItemSchema[];
	timeElapsed: number;
	isLoading?: boolean;
	showCorrectStat?: boolean;
	overallPoints?: OverallPoints;
};

const StatTileCell = ({
	stat,
	isLoading,
}: {
	stat: StatsRowTile;
	isLoading: boolean;
}) => {
	const tile = stat.icons ? (
		<StatTile
			icons={stat.icons}
			label={stat.label}
			value={stat.value}
			suffix={stat.suffix}
			isLoading={isLoading}
			outlined={false}
		/>
	) : (
		<StatTile
			icon={stat.icon}
			label={stat.label}
			value={stat.value}
			suffix={stat.suffix}
			isLoading={isLoading}
			outlined={false}
		/>
	);

	return (
		<Box flex={1} minWidth={0} alignSelf="stretch" style={{ flexBasis: 0 }}>
			{stat.tooltip ? (
				<TooltipWrapper
					title={stat.tooltip}
					display="block"
					style={{ width: "100%", height: "100%" }}
				>
					{tile}
				</TooltipWrapper>
			) : (
				tile
			)}
		</Box>
	);
};

export const SessionReportStatsRow = ({
	isSummativeTest,
	tags,
	topics,
	sessionItems,
	timeElapsed,
	isLoading = false,
	showCorrectStat = false,
	overallPoints,
}: Props) => {
	const { t, i18n } = useTranslation();
	const { isMobile } = useDevice();
	const proficiencyIconMap = useProficiencyIconMap();

	const stats = buildStatsRowTiles({
		isSummativeTest,
		tags,
		topics,
		sessionItems,
		timeElapsed,
		showCorrectStat,
		overallPoints,
		proficiencyIconMap,
		formatTime: formatSecondsToUTCtime,
		locale: i18n.language,
		t,
	});

	if (isMobile) {
		return (
			<Box gap={2} width="100%" minWidth={0} alignSelf="stretch">
				{chunkPairs(stats).map((pair) => (
					<Box
						key={pair.map((stat) => stat.key).join("-")}
						flexDirection="row"
						gap={2}
						width="100%"
						minWidth={0}
						alignItems="stretch"
					>
						{pair.map((stat) => (
							<StatTileCell key={stat.key} stat={stat} isLoading={isLoading} />
						))}
					</Box>
				))}
			</Box>
		);
	}

	return (
		<Box
			flexDirection="row"
			gap={2}
			width="100%"
			minWidth={0}
			alignItems="stretch"
			alignSelf="stretch"
		>
			{stats.map((stat) => (
				<StatTileCell key={stat.key} stat={stat} isLoading={isLoading} />
			))}
		</Box>
	);
};
